import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

export type SiteLocale = "ru" | "en";

type LocaleMessages = {
  nav: {
    homeAriaLabel: string;
    switcherAriaLabel: string;
    about: string;
    services: string;
    contacts: string;
    career: string;
    contactCta: string;
  };
  footer: {
    brandRole: string;
    brandBody: string;
    ctaButton: string;
    companyTitle: string;
    aboutLink: string;
    careerLink: string;
    servicesTitle: string;
    developmentLink: string;
    documentsTitle: string;
    privacyLink: string;
    cookiesLink: string;
    personalDataLink: string;
    contactsTitle: string;
    bottomTagline: string;
  };
  personalDataPage: {
    pageTitle: string;
    badge: string;
    title: string;
    description: string;
    viewerTitle: string;
  };
  policyLayout: {
    documentLabel: string;
    sectionsLabel: string;
    legalRequestSubject: string;
  };
};

type LocaleContextValue = {
  locale: SiteLocale;
  setLocale: (locale: SiteLocale) => void;
  messages: LocaleMessages;
};

const LOCALE_STORAGE_KEY = "catlogic-site-locale";

const MESSAGES: Record<SiteLocale, LocaleMessages> = {
  ru: {
    nav: {
      homeAriaLabel: "Главная страница CatLogic",
      switcherAriaLabel: "Переключить язык",
      about: "О нас",
      services: "Услуги",
      contacts: "Контакты",
      career: "Карьера",
      contactCta: "Связаться",
    },
    footer: {
      brandRole: "Product engineering studio",
      brandBody:
        "Проектируем и запускаем цифровые продукты, которые помогают компаниям работать быстрее, принимать сильнее решения и масштабироваться без лишней технической инерции.",
      ctaButton: "Обсудить проект",
      companyTitle: "Компания",
      aboutLink: "О нас",
      careerLink: "Карьера",
      servicesTitle: "Услуги",
      developmentLink: "Разработка",
      documentsTitle: "Документы",
      privacyLink: "Политика конфиденциальности",
      cookiesLink: "Политика cookie",
      personalDataLink: "Политика обработки персональных данных",
      contactsTitle: "Контакты",
      bottomTagline: "Создаем практичные системы для реальных команд",
    },
    personalDataPage: {
      pageTitle: "Политика обработки персональных данных | CatLogic",
      badge: "Документы",
      title: "Политика обработки персональных данных",
      description: "Порядок обработки и защиты персональных данных пользователей сайта и клиентов CatLogic.",
      viewerTitle: "Политика обработки персональных данных (PDF)",
    },
    policyLayout: {
      documentLabel: "Документ",
      sectionsLabel: "Разделы",
      legalRequestSubject: "CatLogic legal request",
    },
  },
  en: {
    nav: {
      homeAriaLabel: "CatLogic home",
      switcherAriaLabel: "Switch language",
      about: "About",
      services: "Services",
      contacts: "Contacts",
      career: "Careers",
      contactCta: "Contact",
    },
    footer: {
      brandRole: "Product engineering studio",
      brandBody:
        "We design and launch digital products that help companies move faster, make stronger decisions, and scale without unnecessary technical drag.",
      ctaButton: "Discuss your project",
      companyTitle: "Company",
      aboutLink: "About",
      careerLink: "Careers",
      servicesTitle: "Services",
      developmentLink: "Development",
      documentsTitle: "Documents",
      privacyLink: "Privacy policy",
      cookiesLink: "Cookie policy",
      personalDataLink: "Personal data processing policy",
      contactsTitle: "Contacts",
      bottomTagline: "Designing practical systems for real teams",
    },
    personalDataPage: {
      pageTitle: "Personal Data Processing Policy | CatLogic",
      badge: "Documents",
      title: "Personal data processing policy",
      description: "How CatLogic processes and protects the personal data of website users and clients.",
      viewerTitle: "Personal data processing policy (PDF)",
    },
    policyLayout: {
      documentLabel: "Document",
      sectionsLabel: "Sections",
      legalRequestSubject: "CatLogic legal request",
    },
  },
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

const getInitialLocale = (): SiteLocale => {
  if (typeof window === "undefined") {
    return "ru";
  }

  const storedLocale = window.localStorage.getItem(LOCALE_STORAGE_KEY);

  if (storedLocale === "ru" || storedLocale === "en") {
    return storedLocale;
  }

  return "ru";
};

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<SiteLocale>(getInitialLocale);

  useEffect(() => {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  }, [locale]);

  return <LocaleContext.Provider value={{ locale, setLocale, messages: MESSAGES[locale] }}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);

  if (!context) {
    throw new Error("useLocale must be used within LocaleProvider");
  }

  return context;
}
